import {
  type CSSProperties,
  type KeyboardEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { CalendarRangePicker } from './CalendarRangePicker'
import { Icon } from './Icon'

// ── Constants ─────────────────────────────────────────────────────────────────

const POPOVER_WIDTH = 340
const VIEWPORT_MARGIN = 8

// rAF polyfill (mirrors CalendarDayPopover's approach).
const raf =
  typeof window !== 'undefined' &&
  typeof window.requestAnimationFrame === 'function'
    ? window.requestAnimationFrame.bind(window)
    : (cb: FrameRequestCallback): number => {
        const handle = setTimeout(() => cb(performance.now()), 0)
        return handle as unknown as number
      }

const cancelRaf =
  typeof window !== 'undefined' &&
  typeof window.cancelAnimationFrame === 'function'
    ? window.cancelAnimationFrame.bind(window)
    : (handle: number): void => clearTimeout(handle as unknown as NodeJS.Timeout)

// ── Month abbreviations for "DD Mon YYYY" display ─────────────────────────────

const MONTH_ABBR = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
]

/** Format an ISO date (YYYY-MM-DD) as "DD Mon YYYY", e.g. "15 Mar 2027". */
function formatDisplayDate(iso: string): string {
  const parts = iso.split('-').map(Number)
  const y = parts[0]
  const m = parts[1]
  const d = parts[2]
  if (!y || !m || !d) return iso
  const mon = MONTH_ABBR[m - 1] ?? ''
  return `${d} ${mon} ${y}`
}

// ── Props ─────────────────────────────────────────────────────────────────────

export interface DateFieldProps {
  /** ISO YYYY-MM-DD or null. */
  value: string | null
  onChange: (iso: string | null) => void
  /** Placeholder shown when value is null. Default: 'Pick a date'. */
  placeholder?: string | undefined
  /** Min selectable date (ISO). Optional. */
  min?: string | undefined
  /** Max selectable date (ISO). Optional. */
  max?: string | undefined
  /** Disabled state. */
  disabled?: boolean | undefined
  /** Forwarded to the trigger for testing. */
  'data-testid'?: string | undefined
  /** aria-label for the trigger when there is no associated <label>. */
  'aria-label'?: string | undefined
}

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * Single-date picker that wraps CalendarRangePicker in single mode.
 *
 * Clicking anywhere on the trigger (not just a calendar icon) opens a
 * viewport-aware popover. Mirrors the positioning technique used in
 * CalendarDayPopover: two-phase rAF layout with flip-above on bottom overflow.
 *
 * Display format: "DD Mon YYYY". onChange fires the ISO YYYY-MM-DD value.
 */
export function DateField({
  value,
  onChange,
  placeholder = 'Pick a date',
  min,
  max,
  disabled = false,
  'data-testid': testId,
  'aria-label': ariaLabel,
}: DateFieldProps): JSX.Element {
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)

  // ── Open / close helpers ──────────────────────────────────────────────────

  const openPopover = useCallback((): void => {
    if (disabled) return
    setOpen(true)
  }, [disabled])

  const closePopover = useCallback((): void => {
    setOpen(false)
  }, [])

  // ── Viewport-aware positioning (mirrors CalendarDayPopover exactly) ───────

  useLayoutEffect(() => {
    if (!open) {
      setPos(null)
      return
    }
    const anchor = triggerRef.current
    if (!anchor) return

    // Phase 1: render off-screen so the DOM can measure real dimensions.
    setPos({ top: -9999, left: -9999 })

    // Phase 2: measure and reposition after mount.
    const handle = raf(() => {
      const popover = popoverRef.current
      if (!popover) return
      const anchorRect = anchor.getBoundingClientRect()
      const popoverHeight = popover.offsetHeight
      const popoverWidth = popover.offsetWidth

      const maxWidth = window.innerWidth - VIEWPORT_MARGIN * 2
      const effectiveWidth = Math.min(popoverWidth, maxWidth)

      let left = anchorRect.left
      let top = anchorRect.bottom + VIEWPORT_MARGIN

      // Horizontal: shift left when overflowing right edge.
      if (left + effectiveWidth > window.innerWidth - VIEWPORT_MARGIN) {
        left = Math.max(
          VIEWPORT_MARGIN,
          window.innerWidth - effectiveWidth - VIEWPORT_MARGIN,
        )
      }
      if (left < VIEWPORT_MARGIN) left = VIEWPORT_MARGIN

      // Vertical: flip above when overflowing bottom edge.
      if (top + popoverHeight > window.innerHeight - VIEWPORT_MARGIN) {
        top = anchorRect.top - popoverHeight - VIEWPORT_MARGIN
      }
      // Clamp to top of viewport — let popover scroll internally if still overflows.
      if (top < VIEWPORT_MARGIN) top = VIEWPORT_MARGIN

      setPos({ top: top + window.scrollY, left: left + window.scrollX })
    })

    return () => {
      cancelRaf(handle)
    }
  }, [open])

  // ── Keyboard and outside-click dismissal ─────────────────────────────────

  useEffect(() => {
    if (!open) return

    function onKey(e: globalThis.KeyboardEvent): void {
      if (e.key === 'Escape') {
        closePopover()
        triggerRef.current?.focus()
      }
    }

    // Grace period: ignore the click that opened the popover.
    let armed = false
    const armHandle = window.setTimeout(() => { armed = true }, 20)

    function onMouseDown(e: MouseEvent): void {
      if (!armed) return
      const popover = popoverRef.current
      const trigger = triggerRef.current
      const target = e.target as Node
      if (
        (popover && popover.contains(target)) ||
        (trigger && trigger.contains(target))
      ) {
        return
      }
      closePopover()
    }

    window.addEventListener('keydown', onKey)
    window.addEventListener('mousedown', onMouseDown)

    return () => {
      window.clearTimeout(armHandle)
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('mousedown', onMouseDown)
    }
  }, [open, closePopover])

  // ── Keyboard on trigger ───────────────────────────────────────────────────

  function handleTriggerKeyDown(e: KeyboardEvent<HTMLButtonElement>): void {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      if (open) closePopover()
      else openPopover()
    }
  }

  // ── Date selection ────────────────────────────────────────────────────────

  function handleCalendarChange(from: string, _to: string): void {
    onChange(from === '' ? null : from)
    closePopover()
  }

  // ── Clear ─────────────────────────────────────────────────────────────────

  function handleClear(e: React.MouseEvent): void {
    e.stopPropagation()
    onChange(null)
    // Do NOT open the popover — clear is a terminal action.
  }

  // ── Derived display values ────────────────────────────────────────────────

  const displayValue = value ? formatDisplayDate(value) : null
  const isPositioning = pos?.top === -9999

  const effectiveWidth =
    typeof window !== 'undefined'
      ? Math.min(POPOVER_WIDTH, window.innerWidth - VIEWPORT_MARGIN * 2)
      : POPOVER_WIDTH

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      {/* Trigger */}
      <button
        ref={triggerRef}
        type="button"
        role="combobox"
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={ariaLabel}
        data-testid={testId}
        disabled={disabled}
        onClick={openPopover}
        onKeyDown={handleTriggerKeyDown}
        style={triggerStyle(disabled)}
      >
        <span
          style={{
            flex: 1,
            textAlign: 'left',
            color: displayValue ? 'var(--text)' : 'var(--text-3)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {displayValue ?? placeholder}
        </span>

        <span
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexShrink: 0,
          }}
        >
          {value !== null && !disabled ? (
            <span
              role="button"
              aria-label="Clear date"
              tabIndex={0}
              onClick={handleClear}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  e.stopPropagation()
                  onChange(null)
                }
              }}
              style={clearBtnStyle()}
            >
              <Icon name="close" size={14} />
            </span>
          ) : null}
          <Icon
            name="calendar_today"
            size={15}
            style={{ color: disabled ? 'var(--border-strong)' : 'var(--text-3)', flexShrink: 0 }}
          />
        </span>
      </button>

      {/* Popover — rendered via portal so it escapes overflow constraints */}
      {open && pos !== null
        ? createPortal(
            <div
              ref={popoverRef}
              role="dialog"
              aria-label="Pick a date"
              aria-modal="false"
              data-testid="date-field-popover"
              style={{
                position: 'absolute',
                top: pos.top,
                left: pos.left,
                width: effectiveWidth,
                zIndex: 200,
                opacity: isPositioning ? 0 : 1,
                transition: isPositioning ? 'none' : 'opacity 100ms ease-out',
              }}
            >
              <div style={popoverCardStyle()}>
                <CalendarRangePicker
                  from={value ?? ''}
                  to={value ?? ''}
                  onChange={handleCalendarChange}
                  single
                  min={min}
                  max={max}
                />
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  )
}

// ── Style helpers ─────────────────────────────────────────────────────────────

/**
 * Trigger matches the surrounding form inputs in HrEmployeesPage:
 *   padding: 8px 10px, border: 1px solid var(--border), borderRadius: 8,
 *   background: var(--surface), fontSize: 13.
 * Additional: 44px min-height (touch target), flex layout for icon row.
 */
function triggerStyle(disabled: boolean): CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 10px',
    border: '1px solid var(--border)',
    borderRadius: 8,
    background: disabled ? 'var(--surface-2)' : 'var(--surface)',
    fontSize: 13,
    fontFamily: 'inherit',
    cursor: disabled ? 'not-allowed' : 'pointer',
    width: '100%',
    minHeight: 44,
    textAlign: 'left',
    color: 'var(--text)',
    opacity: disabled ? 0.6 : 1,
    // Focus ring — consistent with the rest of the app (--steel-blue ring).
    outline: 'none',
  }
}

function clearBtnStyle(): CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 20,
    height: 20,
    borderRadius: 4,
    cursor: 'pointer',
    color: 'var(--text-3)',
    background: 'transparent',
    padding: 0,
    border: 'none',
    // Ensure clicking the clear icon doesn't bubble to the trigger.
  }
}

function popoverCardStyle(): CSSProperties {
  return {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 12,
    padding: 16,
    boxShadow: 'var(--shadow-modal)',
    overflow: 'hidden',
  }
}
